import { useLocation } from "react-router-dom";
import { useCloudStatus } from "@/hooks/queries/use-cloud";
import { usePageTitle } from "@/hooks/use-page-title";
import { BuildStep } from "@/components/cloud/gcp-wizard/build-step";
import { LiveStep } from "@/components/cloud/gcp-wizard/live-step";
import { PlanStep } from "@/components/cloud/gcp-wizard/plan-step";
import { ProjectStep } from "@/components/cloud/gcp-wizard/project-step";
import { SignInStep } from "@/components/cloud/gcp-wizard/sign-in-step";
import { StartStep } from "@/components/cloud/gcp-wizard/start-step";
import { GcpWizardStepper } from "@/components/cloud/gcp-wizard/wizard-stepper";
import { GCP_WIZARD_BASE, GCP_WIZARD_STEPS, GcpWizardProvider, type GcpWizardStepId } from "@/components/cloud/gcp-wizard/wizard-state";

function currentStep(pathname: string): GcpWizardStepId {
  const rest = pathname.slice(GCP_WIZARD_BASE.length).replace(/^\/|\/$/g, "");
  return GCP_WIZARD_STEPS.find((s) => s.path === rest)?.id ?? "start";
}

/** The six-step Connect Google Cloud wizard, at /connections/google-cloud/*. */
export function GoogleCloudWizardPage() {
  usePageTitle("Connect Google Cloud");
  const { data: status, isLoading } = useCloudStatus();
  const step = currentStep(useLocation().pathname);
  if (isLoading) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  // Only admins can connect; everyone else gets the read-only live view.
  if (status && !status.canManage) {
    return (
      <GcpWizardProvider>
        <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
          <LiveStep />
        </div>
      </GcpWizardProvider>
    );
  }
  return (
    <GcpWizardProvider>
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
        <GcpWizardStepper current={step} />
        {step === "start" && <StartStep />}
        {step === "sign-in" && <SignInStep />}
        {step === "project" && <ProjectStep />}
        {step === "plan" && <PlanStep />}
        {step === "build" && <BuildStep />}
        {step === "live" && <LiveStep />}
      </div>
    </GcpWizardProvider>
  );
}
